import {
  singleAppDraftInputSchema,
  singleAppDraftSchema,
  singleAppDraftResponseSchema,
  syncSingleAppDraftResponseSchema,
  toStandardError,
  type SingleAppDraft,
  type SingleAppDraftInput,
} from "@repo/shared";

import { API_V1_PREFIX, apiRequest, apiRequestData, apiRequestJson } from "../api-client";

export async function getSingleAppDraft(
  connectionId: string,
  appId: string,
): Promise<SingleAppDraft | null> {
  const response = await apiRequestJson({
    path: `${API_V1_PREFIX}/single-app-drafts/${encodeURIComponent(connectionId)}/apps/${encodeURIComponent(appId)}`,
    responseSchema: singleAppDraftResponseSchema,
    badResponseMessage: "Unexpected response from server",
  });

  return response.data;
}

export async function saveSingleAppDraft(
  connectionId: string,
  appId: string,
  input: SingleAppDraftInput,
): Promise<SingleAppDraft | null> {
  const validated = singleAppDraftInputSchema.safeParse(input);
  if (!validated.success) {
    throw toStandardError({
      code: "VALIDATION_ERROR",
      message: "Invalid input",
      status: 400,
      details: validated.error.flatten(),
    });
  }

  return apiRequestData({
    path: `${API_V1_PREFIX}/single-app-drafts/${encodeURIComponent(connectionId)}/apps/${encodeURIComponent(appId)}`,
    method: "PUT",
    body: validated.data,
    dataSchema: singleAppDraftSchema.nullable(),
    badResponseMessage: "Unexpected response from server",
  });
}

export async function clearSingleAppDraft(connectionId: string, appId: string): Promise<void> {
  await apiRequest({
    path: `${API_V1_PREFIX}/single-app-drafts/${encodeURIComponent(connectionId)}/apps/${encodeURIComponent(appId)}`,
    method: "DELETE",
    expectedStatus: 204,
  });
}

export async function syncSingleAppDraft(
  connectionId: string,
  appId: string,
): Promise<{ jobId: string }> {
  return apiRequestData({
    path: `${API_V1_PREFIX}/single-app-drafts/${encodeURIComponent(connectionId)}/apps/${encodeURIComponent(appId)}/sync`,
    method: "POST",
    dataSchema: syncSingleAppDraftResponseSchema.shape.data,
    badResponseMessage: "Unexpected response from server",
  });
}

export async function reconcileSingleAppDraft(jobId: string): Promise<SingleAppDraft | null> {
  return apiRequestData({
    path: `${API_V1_PREFIX}/single-app-drafts/reconcile-job/${encodeURIComponent(jobId)}`,
    method: "POST",
    dataSchema: singleAppDraftSchema.nullable(),
    badResponseMessage: "Unexpected response from server",
  });
}

export type { SingleAppDraft, SingleAppDraftInput };
